import { PencilWithEraserIcon } from "@vericus/cadmus-icons";
import { Button, Divider, Pill, Text } from "@vericus/cadmus-ui";

import { useParams } from "react-router-dom";

import feedbackImg from "@/assets/illustrations/student-running-and-checkmark.png";
import { getFinalDate } from "@/features/assignment";
import {
  AssessmentFragment,
  TaskKind,
  useAssessmentQuery,
  useSetupGroupWorkMutation,
  useWorkGroupQuery,
  WorkSettingsFragment,
} from "@/generated/graphql";
import { useRootNavigate } from "@/routes";
import { formatDateFull } from "@/utils/datetime";
import { formatTaskTitle } from "@/utils/task-title";

import { getWorkStatus, pillColors } from "../utils/utils";
import { ExtensionPill } from "./extension-pill";
import * as styles from "./home-page.css";

export function WritingPage() {
  const { assessmentId = "" } = useParams();
  const { data: assessmentData } = useAssessmentQuery({
    variables: { assessmentId },
  });
  const { data: workGroupData } = useWorkGroupQuery({
    variables: { assessmentId },
  });

  const workGroup = workGroupData?.studentWorkGroup;
  const assessment = assessmentData?.assessment;

  // empty task or group formation in progess
  if (!workGroup || !assessment) return <EmptyTask />;

  // Charter blocks the student from starting once it is usable (enabled AND a
  // published form exists, folded server-side into `groupCharterAvailable`)
  // until this member has accepted it.
  if (
    assessment.settings?.groupCharterAvailable === true &&
    !workGroup.charter?.latestAgreement
  )
    return <CharterNotAccepted />;

  return (
    <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
      <div style={{ flex: 1 }}>
        <GroupEssayCard
          assessment={assessment}
          groupId={workGroup.group.id}
          workId={workGroup.work?.id ?? null}
          finalised={workGroup.finalised}
          resultId={workGroup?.work?.result?.id}
          workSettings={workGroup.work?.settings}
        />
        {assessment.settings?.individualReportEnabled === true &&
          workGroup.individualWork?.id !== undefined && (
            <>
              <div className={styles.essayGap} />
              <IndividualEssayCard
                assessment={assessment}
                workId={workGroup.individualWork.id}
                resultId={workGroup?.individualWork?.result?.id}
                workSettings={workGroup.individualWork.settings}
              />
            </>
          )}
      </div>
      <img src={feedbackImg} height={360} alt="" />
    </div>
  );
}

interface GroupEssayCardProps {
  /** Assessment information */
  assessment: AssessmentFragment;
  /** Group ID */
  groupId: string;
  /** Group Work ID */
  workId: string | null;
  /** Indicates if the group work is finalised */
  finalised: boolean;
  //  Result of the WorkGroup
  resultId?: string;
  /** Work-level settings (may contain a group extended due date) */
  workSettings?: WorkSettingsFragment | null;
}

function GroupEssayCard(props: GroupEssayCardProps) {
  const { assessment, groupId, workId, finalised, resultId, workSettings } =
    props;

  const [dueDate, isExtended] = getFinalDate({
    settings: assessment.settings ?? {},
    workSettings: workSettings ?? null,
    isGroupWork: true,
  });
  const groupOriginalDue = assessment.settings?.dueDate ?? null;

  const rootNavigate = useRootNavigate();
  const [setupWork, { loading }] = useSetupGroupWorkMutation({
    variables: { groupId },
  });

  const workState = getWorkStatus(true, workId, finalised, resultId, dueDate);

  const onStudentEditing = async () => {
    const { data } = await setupWork();
    if (data?.setupGroupWork.work?.id !== undefined) {
      const workId = data.setupGroupWork.work.id;
      rootNavigate(`/work/${workId}/editing`);
    }
  };

  return (
    <div className={styles.essayCard}>
      <div className={styles.essayInfo}>
        <div>
          <Text kind="label">{assessment.subjectName}</Text>
          <Text kind="headingFour">
            {formatTaskTitle(assessment.name, TaskKind.Group)}
          </Text>

          <div className={styles.progress}>
            <Pill basic color={pillColors[workState.status].backgroundColor}>
              <Text
                kind="label"
                style={{ color: pillColors[workState.status].textColor }}
              >
                {workState.label}
              </Text>
            </Pill>
          </div>
        </div>
        <Button
          kind="secondary"
          onClick={onStudentEditing}
          icon={<PencilWithEraserIcon size={24} label="" />}
          disabled={loading}
        >
          Continue Working
        </Button>
      </div>
      <Divider />
      {dueDate !== null && (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Text kind="systemMd">
            <strong>Due:</strong> {formatDateFull(new Date(dueDate))}
          </Text>
          {isExtended && groupOriginalDue !== null && (
            <ExtensionPill
              originalDueDate={groupOriginalDue}
              extendedDueDate={dueDate}
            />
          )}
        </div>
      )}
    </div>
  );
}

interface IndividualEssayCardProps {
  /** Assessment information */
  assessment: AssessmentFragment;
  /** Individual Work ID */
  workId: string;
  //  Result of the Individual Work
  resultId?: string;
  /** Work-level settings (may contain an extended due date) */
  workSettings?: WorkSettingsFragment | null;
}

function IndividualEssayCard(props: IndividualEssayCardProps) {
  const { workId, assessment, resultId, workSettings } = props;

  const [dueDate, isExtended] = getFinalDate({
    settings: assessment.settings ?? {},
    workSettings: workSettings ?? null,
  });
  const individualOriginalDue =
    assessment.settings?.individualReportDueDate ??
    assessment.settings?.dueDate ??
    null;

  const workState = getWorkStatus(false, workId, false, resultId, dueDate);
  const rootNavigate = useRootNavigate();

  return (
    <div className={styles.essayCard}>
      <div className={styles.essayInfo}>
        <div>
          <Text kind="label">{assessment.subjectName}</Text>
          <Text kind="headingFour">
            {formatTaskTitle(assessment.name, TaskKind.Individual)}
          </Text>
          <div className={styles.progress}>
            <Pill basic color={pillColors[workState.status].backgroundColor}>
              <Text
                kind="label"
                style={{ color: pillColors[workState.status].textColor }}
              >
                {workState.label}
              </Text>
            </Pill>
          </div>
        </div>
        <Button
          onClick={() => {
            rootNavigate(`/work/${workId}/editing`);
          }}
          icon={<PencilWithEraserIcon size={24} label="" />}
          kind="secondary"
        >
          Continue Editing
        </Button>
      </div>
      <Divider />
      {dueDate !== null && (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Text kind="systemMd">
            <strong>Due:</strong> {formatDateFull(new Date(dueDate))}
          </Text>
          {isExtended && individualOriginalDue !== null && (
            <ExtensionPill
              originalDueDate={individualOriginalDue}
              extendedDueDate={dueDate}
            />
          )}
        </div>
      )}
    </div>
  );
}

export function EmptyTask() {
  return (
    <div className={styles.root} style={{ alignItems: "center" }}>
      <div className={styles.emptyContent}>
        <img src={feedbackImg} height={280} width={280} alt="" />
        <Text kind="headingThree" textAlign="center">
          You&apos;ll see your group&apos;s assignments here once group
          formation is complete
        </Text>
        <Text kind="bodyLine" textAlign="center">
          Once your group is formed, you can start collaboratively working on
          the your assignment.
        </Text>
      </div>
    </div>
  );
}

export function CharterNotAccepted() {
  return (
    <div className={styles.root} style={{ alignItems: "center" }}>
      <div className={styles.emptyContent}>
        <img src={feedbackImg} height={280} width={280} alt="" />
        <Text kind="headingThree" textAlign="center">
          You&apos;ll see your group&apos;s assignments here once you accept the
          group charter
        </Text>
        <Text kind="bodyLine" textAlign="center">
          Once the group charter is accepted, you can start collaboratively
          working on the your assignment.
        </Text>
      </div>
    </div>
  );
}
